import React from "react";
import {useNavigate} from "react-router-dom";
import {Box, Button, Container, Grid, Paper, Stack, Typography} from "@mui/material";
import {Apartment, Engineering} from "@mui/icons-material";
import {useUserAuth} from "../../context/UserAuthContext";

const PRIMARY = "#3E4F88";

/**
 * Hero da homepage (antes do bloco de apoio) — versão compacta:
 * apresenta os dois caminhos da plataforma (associação em destaque e
 * voluntário/a) + atalho para o login (apenas quando não há sessão).
 */
const Hero = () => {

    const navigate = useNavigate();
    const {user} = useUserAuth();

    // Com sessão iniciada o herói não faz sentido (a homepage serve o utilizador
    // logado a partir do cabeçalho: Portal/Dashboard + Sair).
    if (user) {
        return null;
    }

    return (
        <Box
            sx={{
                bgcolor: "#f4f5fa",
                pt: 16, // espaço para o cabeçalho fixo (logo grande)
                pb: 5,
            }}
        >
            <Container maxWidth="md">
                <Stack spacing={1} sx={{mb: 4, textAlign: "center"}}>
                    <Typography variant="h5" sx={{fontWeight: 700, color: PRIMARY}}>
                        Engenharia ao serviço do impacto social
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{maxWidth: 640, mx: "auto"}}
                    >
                        Ligamos associações que precisam de apoio técnico a engenheiros/as e
                        estudantes que querem fazer a diferença.
                    </Typography>
                </Stack>

                <Grid container spacing={2}>
                    {/* Associação — em destaque */}
                    <Grid item xs={12} md={6}>
                        <Paper
                            sx={{
                                height: "100%",
                                p: 3,
                                borderRadius: 2,
                                bgcolor: PRIMARY,
                                color: "white",
                            }}
                        >
                            <Stack spacing={1.5} sx={{height: "100%", justifyContent: "space-between"}}>
                                <Stack spacing={0.75}>
                                    <Apartment sx={{fontSize: 32, color: "white"}}/>
                                    <Typography variant="h6" sx={{fontWeight: 700}}>
                                        Sou uma associação
                                    </Typography>
                                    <Typography variant="body2">
                                        Apresenta um desafio que precisa de Engenharia.
                                    </Typography>
                                </Stack>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate("/registo/associacao")}
                                    sx={{
                                        bgcolor: "white",
                                        color: PRIMARY,
                                        fontWeight: 600,
                                        "&:hover": {bgcolor: "#e8eaf3"},
                                    }}
                                >
                                    Registar associação
                                </Button>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Voluntário/a */}
                    <Grid item xs={12} md={6}>
                        <Paper
                            sx={{
                                height: "100%",
                                p: 3,
                                borderRadius: 2,
                                border: 1,
                                borderColor: "divider",
                                bgcolor: "background.paper",
                            }}
                        >
                            <Stack spacing={1.5} sx={{height: "100%", justifyContent: "space-between"}}>
                                <Stack spacing={0.75}>
                                    <Engineering sx={{fontSize: 32, color: PRIMARY}}/>
                                    <Typography variant="h6" sx={{fontWeight: 700}}>
                                        Sou engenheiro/a ou estudante
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Coloca as tuas competências ao serviço da comunidade.
                                    </Typography>
                                </Stack>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate("/registo/engenheiro")}
                                    sx={{
                                        color: PRIMARY,
                                        borderColor: PRIMARY,
                                        fontWeight: 600,
                                        "&:hover": {bgcolor: "#f4f5fa"},
                                    }}
                                >
                                    Quero ser voluntário/a
                                </Button>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>

                {user ? null : (
                    <Typography sx={{mt: 3, textAlign: "center"}}>
                        Já tens conta?{" "}
                        <Button
                            size="small"
                            onClick={() => navigate("/login")}
                            sx={{
                                textTransform: "none",
                                color: PRIMARY,
                                fontWeight: 600,
                                textDecoration: "underline",
                            }}
                        >
                            Iniciar sessão
                        </Button>
                    </Typography>
                )}
            </Container>
        </Box>
    );
};

export default Hero;
